import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
const TalkingAboutTRD = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "How to Talk About Treatment-Resistant Depression | Neuro Wellness TMS Centers of America"
        }
        description={
          "Practical guidance for families on how to bring up treatment-resistant depression with someone you care about. What to say, what not to say, and when to try."
        }
        canonical={"/talking-about-treatment-resistant-depression"}
        keywords={
          "how to talk to someone about depression treatment, husband won't get help for depression, supporting a spouse with depression"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "How to Talk to Someone You Love About Treatment-Resistant Depression",
          description:
            "Practical guidance for families on starting conversations about treatment-resistant depression.",
          about: [
            "Treatment-Resistant Depression",
            "Family Support",
            "Mental Health Communication",
          ],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              For Families
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              How to Talk to Someone You Love About Treatment-Resistant
              Depression
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              You've found something that might help and you have no idea how to
              bring it up without making things worse.
            </p>
          </div>
        </section>

        {/* Crisis banner */}
        <section className="bg-accent/10 border-b border-accent/20">
          <div className="container-px mx-auto max-w-3xl py-4 text-center">
            <p className="text-[15px] leading-relaxed text-primary">
              If you're in crisis or need someone to talk to right now, call or
              text{" "}
              <a
                href="tel:988"
                className="font-bold text-accent underline decoration-accent/50 underline-offset-2"
              >
                988
              </a>{" "}
              any time — the Suicide and Crisis Lifeline is available 24/7. If
              you're in immediate danger, call{" "}
              <a
                href="tel:911"
                className="font-bold text-accent underline decoration-accent/50 underline-offset-2"
              >
                911
              </a>
              .
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <p>
                This conversation is hard for a specific reason. The person
                you're talking to has been disappointed before. They've tried
                medications that didn't work, or worked for a while and stopped.
                They may have gone through therapy. They've built up a quiet
                expectation that the next thing won't work either.
              </p>
              <p>
                So when you bring up one more option, it doesn't land as hope.
                It can land as pressure. Or as an implication that they haven't
                tried hard enough. Or as you telling them they're broken and
                need fixing.
              </p>
              <p>
                None of that is what you mean. But it's what they might hear.
                The way you bring it up matters more than what you bring up.
              </p>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Language that tends to work
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>You might say:</p>
                <div className="space-y-3">
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                    <p className="text-[16px] italic leading-relaxed text-primary">
                      "I found something I didn't know existed. I'm not saying
                      you should do it. I'm saying I want to understand it, and
                      I'd like you to look at it with me."
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                    <p className="text-[16px] italic leading-relaxed text-primary">
                      "You don't have to decide anything. I just want to put
                      this on the table so it's there."
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                    <p className="text-[16px] italic leading-relaxed text-primary">
                      "I know you've tried a lot. This is different from what
                      you've done before, and I can explain why if you want. Or
                      not. No rush."
                    </p>
                  </div>
                </div>
                <p>
                  Notice what these have in common: they remove pressure, they
                  acknowledge what they've already been through, and they leave
                  the decision with them.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Language that tends not to work
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>You might be tempted to say:</p>
                <div className="space-y-3">
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-5">
                    <p className="text-[16px] italic leading-relaxed text-muted-foreground">
                      "You have to try this — it's supposed to work really
                      well."
                    </p>
                    <p className="mt-2 text-[15px] text-muted-foreground">
                      This sounds like a sales pitch and sets up an expectation
                      that will feel like failure if it doesn't work.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-5">
                    <p className="text-[16px] italic leading-relaxed text-muted-foreground">
                      "I've been reading about this and I really think you
                      should look into it."
                    </p>
                    <p className="mt-2 text-[15px] text-muted-foreground">
                      This positions you as the expert and them as the one who
                      needs to be educated. It's subtle, but people who've been
                      through treatment feel it.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-5">
                    <p className="text-[16px] italic leading-relaxed text-muted-foreground">
                      "What do you have to lose?"
                    </p>
                    <p className="mt-2 text-[15px] text-muted-foreground">
                      They know what they have to lose. They've lost time, hope,
                      and trust in treatments before. This question minimizes
                      that.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Timing and setting
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  Don't bring it up during a crisis. If they're in a bad place
                  right now, this conversation will wait. The right time is a
                  calm evening, a quiet weekend morning — a moment when things
                  are stable enough that the conversation itself doesn't add to
                  the load.
                </p>
                <p>
                  Don't ambush them with it. Don't bring it up in front of other
                  people. Don't text it and leave it hanging. This is a
                  conversation that belongs in person, in private, with no time
                  pressure.
                </p>
                <p>
                  If you live together, pick a time when neither of you is
                  rushing somewhere. If you don't live together, ask if you can
                  come over, or if they'd be open to a phone call. Don't send a
                  wall of links in an email and hope they read them.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What to do if they say no
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  Accept it. A no today is not permanent. The seed is planted.
                  They may come back to it in a week, a month, or six months —
                  and when they do, the fact that you didn't push will make it
                  easier for them to raise it.
                </p>
                <p>
                  You might say: "That's fine. I'll leave the information here.
                  If you ever want to look at it, it's there, and if you want me
                  to go with you to ask questions, I will."
                </p>
                <p>
                  Then drop it. Genuinely drop it. If you keep bringing it up,
                  it becomes a thing between you, and the next time it comes up
                  it will carry that weight.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                "Treatment-resistant" is not a verdict
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  If the term "treatment-resistant depression" comes up, it's
                  worth knowing what it means and doesn't mean. It's a clinical
                  category describing the treatments, not the person. It means
                  the standard medications haven't worked adequately — not that
                  the person is somehow resistant to getting better.
                </p>
                <p>
                  It's a description of a mismatch between a class of drugs and
                  a specific brain. It doesn't mean they're difficult. It
                  doesn't mean they're not trying. It means the mechanism those
                  drugs target isn't the one that needs targeting.
                </p>
                <p>
                  If it helps, you can frame it that way: "The doctor said the
                  medications you've tried all work on the same system in the
                  brain. This works on a different one. That's why it exists."
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                When to seek immediate help
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  If the person you're concerned about is talking about not
                  wanting to be here, expressing thoughts of self-harm, or seems
                  to be in acute distress, don't wait for the right moment to
                  bring up treatment options. Call or text{" "}
                  <a href="tel:988" className="font-bold text-accent underline">
                    988
                  </a>{" "}
                  — the Suicide and Crisis Lifeline is available 24/7. If
                  there's immediate danger, call{" "}
                  <a href="tel:911" className="font-bold text-accent underline">
                    911
                  </a>
                  .
                </p>
                <p>
                  This isn't a substitute for the conversation above. It's a
                  different situation, and it takes priority.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                If they want to learn more
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                <Link to="/what-is-tms" className="text-accent hover:underline">
                  What Is TMS? A Plain Explanation for Families →
                </Link>
                <Link
                  to="/for-families"
                  className="text-accent hover:underline"
                >
                  For Families and Caregivers →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default TalkingAboutTRD;
